import { ResponsiveImage } from "@/components/site/ResponsiveImage";
import { Reveal } from "@/components/site/Reveal";
import { shopFloor } from "@/lib/business";
import { clevorImage, heroImage, imageSizes, shopSignImage } from "@/lib/images";
import type { PictureAsset } from "@/lib/images";

const shopImages: Record<string, { image: PictureAsset; alt: string; sizes: string }> = {
  "Clevor Engine": {
    image: clevorImage,
    alt: "Blue Clevor engine with aluminum Cleveland heads, Trick Flow water neck, and gold rockers",
    sizes: imageSizes.shopCard,
  },
};

const machinePhotos: Record<string, { src: string; alt: string; width: number; height: number }> = {
  "Seat & Valve Guide Machine": {
    src: "/images/shop-tcm25-peterson.webp",
    alt: "TCM-25 Peterson machine in the shop",
    width: 768,
    height: 576,
  },
  "Rotating & Balancing Machine": {
    src: "/images/shop-balancer.webp",
    alt: "Rotating and balancing machine with a crankshaft set up in the shop",
    width: 480,
    height: 360,
  },
  "Machine Lathe": {
    src: "/images/shop-leblond-lathe.webp",
    alt: "LeBlond machine lathe in the shop",
    width: 480,
    height: 360,
  },
  "Cylinder Honing Machine": {
    src: "/images/shop-winona-auto-hone.webp",
    alt: "Winona Van Norman BE 3301 Auto Hone with an engine block",
    width: 600,
    height: 450,
  },
  "Magnaflux Machine": {
    src: "/images/shop-magnaflux.webp",
    alt: "Magnaflux machine inspecting a crankshaft for cracks",
    width: 1024,
    height: 768,
  },
};

export function ShopFloor() {
  return (
    <section
      id="shop"
      className="scroll-mt-28 border-b border-border py-20 sm:py-28"
      aria-labelledby="shop-heading"
    >
      <div className="mx-auto max-w-6xl px-4">
        <Reveal>
          <p className="eyebrow">Inside the shop</p>
          <h2 id="shop-heading" className="mt-3 text-4xl uppercase sm:text-5xl">
            The machines behind the work
          </h2>
          <p className="mt-4 max-w-2xl text-muted-foreground">
            Dedicated equipment for valve seats and guides, cylinder honing, rotating assembly
            balance, lathe work, magnaflux crack detection, and the custom engine builds that come
            through Enterprise — including Clevor short blocks and head CCing on the stand.
          </p>
        </Reveal>

        <div className="mt-12 grid gap-4 md:grid-cols-3">
          <Reveal
            variant="image"
            as="figure"
            className="relative overflow-hidden rounded-lg border border-border md:col-span-2"
          >
            <ResponsiveImage
              image={heroImage}
              alt="Engine block being precision machined at Champion Automotive Machine Shop"
              sizes={imageSizes.shopWide}
              imgClassName="h-72 w-full object-cover sm:h-80"
              style={{ boxShadow: "var(--shadow-hard)" }}
            />
            <figcaption className="absolute bottom-3 left-3 rounded-sm bg-background/80 px-2.5 py-1 text-[0.65rem] font-semibold tracking-[0.18em] uppercase backdrop-blur">
              Block work
            </figcaption>
          </Reveal>
          <Reveal
            variant="image"
            as="figure"
            delay={90}
            className="relative overflow-hidden rounded-lg border border-border"
          >
            <ResponsiveImage
              image={shopSignImage}
              alt="Champion Automotive Machine Shop sign in Enterprise, Alabama"
              sizes={imageSizes.shopSide}
              imgClassName="h-72 w-full object-cover object-[center_28%] sm:h-80"
              style={{ boxShadow: "var(--shadow-hard)" }}
            />
            <figcaption className="absolute bottom-3 left-3 rounded-sm bg-background/80 px-2.5 py-1 text-[0.65rem] font-semibold tracking-[0.18em] uppercase backdrop-blur">
              The shop
            </figcaption>
          </Reveal>
        </div>

        <ul className="mt-6 grid gap-5 sm:grid-cols-2">
          {shopFloor.map((item, index) => {
            const photo = shopImages[item.title];
            const machinePhoto = machinePhotos[item.title];
            return (
              <Reveal key={item.title} as="li" delay={index * 90} className="h-full">
                <article className="surface-card flex h-full flex-col overflow-hidden">
                  {photo ? (
                    <div className="relative aspect-[4/3] overflow-hidden border-b border-border">
                      <ResponsiveImage
                        image={photo.image}
                        alt={photo.alt}
                        sizes={photo.sizes}
                        imgClassName="size-full object-cover"
                      />
                    </div>
                  ) : machinePhoto ? (
                    <div className="relative aspect-[4/3] overflow-hidden border-b border-border">
                      <img
                        src={machinePhoto.src}
                        alt={machinePhoto.alt}
                        width={machinePhoto.width}
                        height={machinePhoto.height}
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 size-full object-cover object-center"
                      />
                    </div>
                  ) : null}
                  <div className="flex flex-1 flex-col p-6">
                    <p className="text-[0.65rem] font-semibold tracking-[0.2em] text-primary-text uppercase">
                      {item.tag}
                    </p>
                    <h3 className="mt-3 text-2xl uppercase">{item.title}</h3>
                    <p className="mt-2 font-display text-3xl text-steel">{item.machine}</p>
                    <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                      {item.body}
                    </p>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
